import { type ChangeEvent, forwardRef, useEffect, useState } from 'react';

import { Input } from '@/ui/components/input';
import { cls } from '@/utils/cls';

type TimeInputProps = {
  value?: string;
  onChange?: (value: string) => void;
  error?: string;
} & Omit<
  React.ComponentProps<'input'>,
  | 'type'
  | 'value'
  | 'onChange'
  | 'inputMode'
  | 'onBlur'
  | 'onKeyDown'
  | 'onFocus'
>;

export const TimeInput = forwardRef<HTMLInputElement, TimeInputProps>(
  (
    {
      id,
      value = '',
      onChange,
      placeholder = 'HH:MM',
      className,
      error,
      ...props
    },
    ref,
  ) => {
    const { ref: _ref, ...restProps } = props as any;
    const [inputValue, setInputValue] = useState(value);
    const [isValid, setIsValid] = useState(true);
    const [internalError, setInternalError] = useState<string | undefined>(
      error,
    );
    const [isTouched, setIsTouched] = useState(false);
    const [isFocused, setIsFocused] = useState(false);

    useEffect(() => {
      setInputValue(value);
    }, [value]);

    useEffect(() => {
      setInternalError(error);
    }, [error]);

    const formatTimeString = (value: string): string => {
      // Remove all non-digit characters
      const digits = value.replace(/\D/g, '');

      // Format based on the number of digits
      if (digits.length <= 2) {
        return digits;
      } else {
        const hours = digits.substring(0, 2);
        const minutes = digits.substring(2, 4);
        return `${hours}:${minutes}`;
      }
    };

    const validateTime = (timeString: string): boolean => {
      // Empty is valid (unless required, but that's handled by the required attribute)
      if (!timeString) return true;

      // Check format
      const timeRegex = /^([0-1]?[0-9]|2[0-3]):([0-5][0-9])$/;
      if (!timeRegex.test(timeString)) return false;

      // Extract hours and minutes
      const [hours, minutes] = timeString.split(':').map(Number);

      // Validate hours and minutes
      return hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59;
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      const rawValue = e.target.value;

      // Format the input
      const formattedValue = formatTimeString(rawValue);
      setInputValue(formattedValue);

      // Validate the input
      const valid = validateTime(formattedValue);
      setIsValid(valid);
      setInternalError(valid ? undefined : 'Invalid time format');

      // Call the onChange handler with the formatted value
      if (onChange) {
        onChange(formattedValue);
      }
    };

    const formatIncompleteTime = (value: string): string => {
      const [hours, minutes] = value.split(':');

      if (hours && minutes) {
        // Handle case where both hours and minutes are provided
        const parsedHours = parseInt(hours, 10);
        let parsedMinutes = parseInt(minutes, 10);

        if (parsedHours >= 0 && parsedHours <= 23) {
          if (
            minutes.length === 1 &&
            parsedMinutes >= 0 &&
            parsedMinutes <= 5
          ) {
            // If single digit minute is 0-5, interpret as tens of minutes
            parsedMinutes *= 10;
          } else if (parsedMinutes > 59) {
            // If minutes are invalid, default to 00
            parsedMinutes = 0;
          }
          return `${parsedHours.toString().padStart(2, '0')}:${parsedMinutes.toString().padStart(2, '0')}`;
        }
      } else if (hours && !minutes) {
        // Handle case where only hours are provided
        const parsedHours = parseInt(hours, 10);
        if (parsedHours >= 0 && parsedHours <= 23) {
          return `${parsedHours.toString().padStart(2, '0')}:00`;
        }
      }

      // If it doesn't fit the above cases, return the original formatted string
      return formatTimeString(value);
    };

    const handleBlur = () => {
      setIsTouched(true);
      setIsFocused(false);

      // Format incomplete time
      const formattedValue = formatIncompleteTime(inputValue);
      setInputValue(formattedValue);

      // Validate the formatted value
      const valid = validateTime(formattedValue);
      setIsValid(valid);
      setInternalError(valid ? undefined : 'Invalid time format');

      // Call the onChange handler with the formatted value
      if (onChange) {
        onChange(formattedValue);
      }
    };

    const handleFocus = () => {
      setIsFocused(true);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        const formattedValue = formatIncompleteTime(inputValue);
        setInputValue(formattedValue);

        const valid = validateTime(formattedValue);
        setIsValid(valid);
        setInternalError(valid ? undefined : 'Invalid time format');

        if (onChange) {
          onChange(formattedValue);
        }
      }
    };
    const showError = !isValid && isTouched && !isFocused;
    return (
      <div className={cls('space-y-2', className)}>
        <Input
          ref={ref}
          id={id}
          type="text"
          inputMode="numeric"
          value={inputValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          maxLength={5}
          className={cls(
            'text-neutral-700 focus-visible:border-neutral-200 focus-visible:ring-2 focus-visible:ring-neutral-950/50',
            {
              'border-red-500 focus-visible:ring-red-500': showError,
            },
          )}
          aria-invalid={showError}
          aria-describedby={showError ? `${id}-error` : undefined}
          {...restProps}
        />
        {showError && internalError && (
          <p id={`${id}-error`} className="text-xs text-red-500">
            {internalError}
          </p>
        )}
      </div>
    );
  },
);

TimeInput.displayName = 'TimeInput';
